import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useDebounce } from "use-debounce";

import { Content } from "../components";
import {
  Table,
  TableBody,
  TableButtons,
  TableHead,
  TableNoContent,
} from "../components/NewTable";
import CheckboxInput from "../components/Modal/CheckBoxInput/index";
import Pagination from "../components/NewTable/Pagination";
import ListHeader from "../components/ListHeader/ListHeader";
import TableSkeleton from "../Skeletons/TableSkeleton";
import { Chip } from "../pageComponents/BillingList";
import Modal from "../components/Modal/Modal";
import ModalHeading from "../components/Modal/ModalHeading";
import ModalForm from "../components/Modal/ModalForm";
import InputsSection from "../components/Modal/InputsSection";
import ModalButtons from "../components/Modal/ModalButtons";
import FloatingTimer from "../components/AccordionRow/FloatingTimer";
import PreviewModalVersion2 from "../pageComponents/Patients/PreviewModalVersion2";

import classes from "../styles/BiilingList.module.css";
import tableClasses from "../styles/NewTable.module.css";

import { BillingListTableHeadData } from "../hooks/billingList/staticData";
import useFetchLatestInactiveBilling from "../hooks/billingList/useFetchLatestInactiveBilling";
import useFetchPreviousBilling, {
  IPreviousBillingItem,
} from "../hooks/billingList/useFetchPreviousBilling";
import useMarkBillingCycleAsBilled from "../hooks/billingList/useMarkBillingCycleAsBilled";
import { useFetchAssociatedClinic, useFetchStatus } from "../hooks/patients";
import useFloatingTimer from "../hooks/patients/useFloatingTimer";
import useFetchBillingCycleDaysLeft from "../hooks/billingList/useFetchBillingCycleDaysLeft";
import { IPatient } from "../hooks/patients/index.type";

interface RootState {
  loginReducer: {
    userDetails: {
      status: "success" | "fail";
      token: string;
      data?: {
        user?: {
          clinic?: {
            _id: string;
          };
        };
      };
    };
  };
}

export interface PreviewModalState {
  status: boolean;
  id: string;
}

interface IPatient1 {
  _id: string;
  name: string;
  dob: string;
  dobString: string;
}

const BillingList: React.FC = () => {
  const userDetails = useSelector(
    (state: RootState) => state.loginReducer?.userDetails
  );
  const clinicId = userDetails?.data?.user?.clinic?._id || "";

  // state
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const { fetchStatus, status } = useFetchStatus();
  const [pageNumber, setPageNumber] = useState<number>(1);

  const perpageData = 10;
  const [searchKey, setSearchKey] = React.useState<string>("");
  const [debouncedSearchKey] = useDebounce(searchKey, 300);
  const [expandedPatientId, setExpandedPatientId] = useState<string | null>(
    null
  );
  const [mockedData, setMockedData] = useState<any>(null);
  const billing_data_end_ref = useRef<HTMLDivElement>(null);
  const [showFloatingTimer, setShowFloatingTimer] = useState(false); // floating timer
  const [sortConfig, setSortConfig] = React.useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "",
    direction: "asc",
  });
  const [showPhysicianReportModal, setShowPhysicianReportModal] = useState<{
    status: boolean;
    patient?: IPatient1;
    billingCycleId?: string;
    date?: string;
  }>({ status: false });

  const [selectedDate, setSelectedDate] = useState<{
    date: string;
    billingCycleId: string;
  } | null>(null);

  const { associatedClinic, getAssociatedClinic } = useFetchAssociatedClinic({
    clinicId,
  });

  // floating timer
  const { floatingTimer, getFloatingTimer } = useFloatingTimer();

  useEffect(() => {
    getFloatingTimer();
  }, []);

  // visibility when floatingTimer changes
  useEffect(() => {
    if (floatingTimer) {
      setShowFloatingTimer(floatingTimer.isAlreadyStarted === true);
    }
  }, [floatingTimer]);

  const { loadingDaysLeft, daysLeftOptions, getBillingCycleDaysLeft } =
    useFetchBillingCycleDaysLeft(); // Total time filter

  const {
    latestInactiveBillingData,
    latestInactiveBillingLoading,
    getLatestInactiveBillingData,
  } = useFetchLatestInactiveBilling({
    search: debouncedSearchKey || undefined,
    billable:
      filters["Billable?"] === "yes"
        ? "yes"
        : filters["Billable?"] === "no"
        ? "no"
        : undefined,
    associatedClinic:
      filters["Clinic"] && filters["Clinic"] !== "Clinic"
        ? filters["Clinic"]
        : undefined,
    timeSpent: filters.timeSpent || undefined,
    page: pageNumber,
    limit: perpageData,
    sort: sortConfig.key,
    order: sortConfig.direction,
  });

  const {
    previousBillingData,
    previousBillingLoading,
    getPreviousBillingData,
  } = useFetchPreviousBilling({ patientId: expandedPatientId || "" });

  // ====================================== Hooks ======================================

  const { markBillingCycleAsBilled, markBilledLoading } =
    useMarkBillingCycleAsBilled();

  const scrollToBottom = () => {
    if (billing_data_end_ref.current) {
      billing_data_end_ref.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (expandedPatientId) {
      scrollToBottom();
    }
  }, [expandedPatientId]);

  useEffect(() => {
    getBillingCycleDaysLeft();
    getAssociatedClinic();
    fetchStatus();
  }, []);

  const onPageChange = (currentPage: { selected: number }) => {
    setPageNumber(currentPage.selected + 1);
  };

  const handleFiltersUpdate = (newFilters: Record<string, string>) => {
    const updatedFilters = { ...newFilters };
    if (updatedFilters["Days Overdue"] === "Days Overdue") {
      delete updatedFilters["Days Overdue"];
    }
    setPageNumber(1);
    setFilters(updatedFilters);
  };

  const handleSearch = (searchTerm: string) => {
    setPageNumber(1);
    setSearchKey(searchTerm);
  };

  const togglePrevBilling = (patientId: string) => {
    setExpandedPatientId((prevId) => (prevId === patientId ? null : patientId));
  };

  const formatDate = (dateString: string) => {
    if (dateString === "NA") return "N/A";
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  const getBillingCyclePeriod = (startDate: string, endDate: string) => {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
    setPageNumber(1);
  };

  const filterOptions = [
    {
      id: "Clinic",
      label: "Clinic",
      value: "Clinic",
      options: [
        { value: "Clinic", label: "Clinic" },
        ...(associatedClinic || []).map((clinic) => ({
          label: clinic.name,
          value: clinic._id,
        })),
      ],
    },
    {
      id: "Billable?",
      label: "Billable?",
      value: "Billable?",
      options: [
        { value: "Billable?", label: "Billable?" },
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
    },
    {
      id: "Total Time",
      disable: false,
      label: "Total Time",
      value: "timeSpent",
      options: [
        { label: "Total Time", value: "" },
        ...(daysLeftOptions || []).map((option) => ({
          label: option.label,
          value: option.value,
        })),
      ],
      loading: loadingDaysLeft,
    },
  ];

  const billingData =
    mockedData?.data?.data || latestInactiveBillingData?.data?.data;
  const isLoading = !mockedData && latestInactiveBillingLoading;

  const [billingConfirmModal, setBillingConfirmModal] = useState<{
    status: boolean;
    patientId?: string;
    billingId?: string;
    newStatus?: string;
    isPrevious?: boolean;
  }>({ status: false });

  const handleBillingConfirm = async () => {
    try {
      if (
        billingConfirmModal.billingId &&
        billingConfirmModal.newStatus === "yes"
      ) {
        await markBillingCycleAsBilled(billingConfirmModal.billingId);
      }

      setBillingConfirmModal({ status: false });

      getLatestInactiveBillingData();
      if (expandedPatientId) {
        getPreviousBillingData();
      }
    } catch (error) {
      console.error("Error in billing confirmation:", error);
      toast.error("Failed to update billing status. Please try again.");
    }
  };

  return (
    <Content currentMenu="BillingList" headerTitle="" list={true}>
      <ListHeader
        title="Billing List"
        searchPlaceholder="Search by Name and Dob...."
        showFilter
        setFilters={handleFiltersUpdate}
        onSearchChange={handleSearch}
        style={{ fontFamily: "Roboto, sans-serif" }}
        filterOptions={filterOptions}
        filters={filters}
        showBillingCycle={false}
      />

      <Table className={`${tableClasses.billingTable} billingTable`}>
        <TableHead
          data={BillingListTableHeadData}
          onSort={handleSort}
          sortKey={sortConfig?.key || ""}
          sortOrder={sortConfig?.direction || "asc"}
          style={{ fontFamily: "Roboto, sans-serif" }}
          rowStyle={{ borderBottom: "none" }}
        />
        <TableBody>
          {isLoading ? (
            <TableSkeleton rows={BillingListTableHeadData.length} columns={5} />
          ) : billingData?.length > 0 ? (
            billingData.map((list: any) => {
              if (!list || !list.patient || !list.associatedClinic) return null;

              return (
                <React.Fragment key={list._id || Math.random()}>
                  {list?._id && (
                    <tr
                      style={{
                        backgroundColor:
                          expandedPatientId === list.patient._id
                            ? "#FEF2F2"
                            : "transparent",
                      }}
                    >
                      <td></td>
                      <td>{list?.billingCycleStartDate || "N/A"}</td>
                      <td
                        style={{
                          fontWeight: 500,
                          textTransform: "capitalize",
                          color: "var(--black-new)",
                        }}
                      >
                        {list?.patient?.name || "N/A"}
                      </td>
                      <td>
                        {list?.patient?.dobString
                          ? formatDate(list.patient.dobString)
                          : "N/A"}
                      </td>
                      <td>{list?.associatedClinic?.name || "N/A"}</td>
                      <td>
                        <Chip
                          label={
                            list.billStatus === "billed"
                              ? "Billed"
                              : list.billStatus === "yes"
                              ? "Yes"
                              : "No"
                          }
                          variant={
                            list.billStatus === "billed"
                              ? "warning"
                              : list.billStatus === "yes"
                              ? "success"
                              : "danger"
                          }
                        />
                      </td>
                      {/* <td>
                        {list.billStatus === "no" ? (
                          <p style={{ color: "var(--grey-new2)" }}>N/A</p>
                        ) : (
                          <p
                            style={{
                              color:
                                list.overdueDays > 0
                                  ? "var(--red-new)"
                                  : "var(--grey-new2)",
                              fontWeight: list.overdueDays > 0 ? 500 : 400,
                            }}
                          >
                            {list.overdueDays} days
                          </p>
                        )}
                      </td> */}
                      <td>
                        <Chip
                          label={list.isPhoneCall === "yes" ? "Yes" : "No"}
                          variant={
                            list.isPhoneCall === "yes" ? "success" : "danger"
                          }
                        />
                      </td>
                      <TableButtons
                        customStyle={{
                          width: "3rem",
                          textAlign: "center",
                          margin: "1rem",
                        }}
                        buttons={[
                          {
                            icon: "pdf-icon-1",
                            onClick: () =>
                              setShowPhysicianReportModal({
                                status: true,
                                patient: list.patient,
                                billingCycleId: list._id,
                                date: list.billingCycleStartDate,
                              }),
                            disabled:
                              list.transmissionDate === "NA" ||
                              list.transmissionDate === "N/A",
                            //disabled: list.billStatus === "no"
                          },
                        ]}
                      />
                      <td>{list.consolidatedBillingMinutes}m</td>
                      <td>{formatDate(list.transmissionDate)}</td>
                      <td>
                        <button
                          className={classes.toggle_btn}
                          onClick={() => togglePrevBilling(list.patient._id)}
                        >
                          {expandedPatientId === list.patient._id
                            ? "Hide"
                            : "View"}
                        </button>
                      </td>
                      <td>
                        {/* <CheckboxInput
                          label="Billed"
                          checked={
                            list.billStatus === "billed"
                          }
                          onChange={(e) => {
                            const newStatus = e.target.checked ? "yes" : "no";
                            setBillingConfirmModal({
                              status: true,
                              patientId: list.patient._id,
                              billingId: list._id,
                              newStatus,
                              isPrevious: false,
                            });

                            e.target.checked = !e.target.checked;
                          }}
                          checkboxStyle={{
                            accentColor: "var(--primary-color)",
                            width: "1.3rem",
                            height: "1.3rem",
                          }}
                        /> */}

                        <CheckboxInput
                          label="Billed"
                          checked={list.billStatus === "billed"}
                          disabled={
                            list.billStatus === "billed" ||
                            list.billStatus === "no"
                          }
                          onChange={(e) => {
                            if (
                              list.billStatus !== "billed" &&
                              e.target.checked
                            ) {
                              const newStatus = "yes";

                              setBillingConfirmModal({
                                status: true,
                                patientId: list.patient._id,
                                billingId: list._id,
                                newStatus,
                                isPrevious: false,
                              });
                            }

                            // Prevent unchecking after checked
                            if (
                              list.billStatus === "billed" &&
                              !e.target.checked
                            ) {
                              e.preventDefault(); // for native checkboxes
                            }
                          }}
                          checkboxStyle={{
                            accentColor: "var(--primary-color)",
                            width: "1.3rem",
                            height: "1.3rem",
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  {expandedPatientId === list.patient._id && (
                    <>
                      {previousBillingLoading && (
                        <tr>
                          <td
                            colSpan={BillingListTableHeadData.length}
                            style={{ padding: "0" }}
                          >
                            <TableSkeleton
                              rows={2}
                              columns={BillingListTableHeadData.length}
                            />
                          </td>
                        </tr>
                      )}

                      {previousBillingData?.data &&
                      previousBillingData.data.data.length > 0
                        ? previousBillingData.data.data.map(
                            (prevBilling: IPreviousBillingItem) => (
                              <tr key={prevBilling._id}>
                                <td></td>

                                <td>
                                  {formatDate(
                                    prevBilling.billingCycleStartDate
                                  )}
                                </td>

                                <td></td>
                                <td></td>
                                <td></td>

                                <td>
                                  <Chip
                                    label={
                                      prevBilling.billStatus === "billed"
                                        ? "Billed"
                                        : prevBilling.billStatus === "yes"
                                        ? "Yes"
                                        : "No"
                                    }
                                    variant={
                                      prevBilling.billStatus === "billed"
                                        ? "warning"
                                        : prevBilling.billStatus === "yes"
                                        ? "success"
                                        : "danger"
                                    }
                                  />
                                </td>

                                <td>
                                  <Chip
                                    label={
                                      list.isPhoneCall === "yes" ? "Yes" : "No"
                                    }
                                    variant={
                                      list.isPhoneCall === "yes"
                                        ? "success"
                                        : "danger"
                                    }
                                  />
                                </td>

                                <TableButtons
                                  customStyle={{
                                    width: "3rem",
                                    textAlign: "center",
                                    margin: "1rem",
                                  }}
                                  buttons={[
                                    {
                                      icon: "pdf-icon-1",
                                      onClick:
                                        prevBilling.billStatus === "billed" ||
                                        prevBilling.billStatus === "yes"
                                          ? () =>
                                              setShowPhysicianReportModal({
                                                status: true,
                                                patient: list.patient,
                                                billingCycleId: prevBilling._id,
                                                date: prevBilling.billingCycleStartDate,
                                              })
                                          : undefined,
                                      disabled: !(
                                        prevBilling.billStatus === "billed" ||
                                        prevBilling.billStatus === "yes"
                                      ),
                                    },
                                  ]}
                                />

                                <td>
                                  {prevBilling.consolidatedBillingMinutes}m
                                </td>
                                <td>
                                  {formatDate(prevBilling.transmissionDate)}
                                </td>

                                <td></td>

                                <td>
                                  <CheckboxInput
                                    label="Billed"
                                    checked={
                                      prevBilling.billStatus === "billed"
                                    }
                                    disabled={
                                      prevBilling.billStatus === "billed" ||
                                      prevBilling.billStatus === "no"
                                    }
                                    onChange={(e) => {
                                      if (
                                        prevBilling.billStatus !== "billed" &&
                                        e.target.checked
                                      ) {
                                        const newStatus = "yes";
                                        setBillingConfirmModal({
                                          status: true,
                                          patientId: list.patient._id,
                                          billingId: prevBilling._id,
                                          newStatus,
                                          isPrevious: true,
                                        });
                                      }

                                      // Prevent unchecking after checked
                                      if (
                                        prevBilling.billStatus === "billed" &&
                                        !e.target.checked
                                      ) {
                                        e.preventDefault(); // for native checkboxes
                                      }
                                    }}
                                    checkboxStyle={{
                                      accentColor: "var(--primary-color)",
                                      width: "1.3rem",
                                      height: "1.3rem",
                                    }}
                                  />
                                </td>
                              </tr>
                            )
                          )
                        : !previousBillingLoading && (
                            <TableNoContent
                              colSpan={BillingListTableHeadData.length}
                              message="No previous billing records found"
                            />
                          )}

                      <div ref={billing_data_end_ref} />
                    </>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <TableNoContent
              colSpan={BillingListTableHeadData.length}
              message="No data found"
            />
          )}
        </TableBody>
      </Table>

      <Pagination
        style={{ fontFamily: "Roboto, sans-serif" }}
        itemsPerPage={perpageData}
        totalItems={latestInactiveBillingData?.totalResults || 0}
        onPageChange={onPageChange}
        currentPage={pageNumber}
      />
      {showPhysicianReportModal.status && showPhysicianReportModal.patient && (
        <PreviewModalVersion2
          show={true}
          onClose={() => {
            setShowPhysicianReportModal({
              status: false,
              date: undefined,
              billingCycleId: undefined,
            });
            setSelectedDate(null);
          }}
          setSelectedDate={setSelectedDate}
          selectedDate={selectedDate}
          patient={showPhysicianReportModal.patient as IPatient}
          initialCycle={{
            billingCycleId: showPhysicianReportModal.billingCycleId!,
            date: showPhysicianReportModal.date!,
          }}
        />
      )}

      {showFloatingTimer && floatingTimer && (
        <FloatingTimer floatingTimer={floatingTimer} />
      )}

      <Modal show={billingConfirmModal.status}>
        <ModalHeading
          heading={
            billingConfirmModal.newStatus === "yes"
              ? "Mark as Billed"
              : "Mark as Not Billed"
          }
        />
        <ModalForm
          style={{ marginBottom: "2.5rem" }}
          onSubmit={(e) => {
            e.preventDefault();
            handleBillingConfirm();
          }}
        >
          <InputsSection style={{ width: "34rem" }}>
            <p className="primaryText">
              Are you sure you want to{" "}
              {billingConfirmModal.newStatus === "yes"
                ? "mark as billed"
                : "mark as not billed"}
              ?
              <br />
              <br />
              <span className="dangerText">This action cannot be undone.</span>
            </p>
          </InputsSection>

          <ModalButtons
            submitButtonLabel={
              billingConfirmModal.newStatus === "yes"
                ? "Mark as Billed"
                : "Mark as Not Billed"
            }
            cancelButtonLabel="Cancel"
            onCancel={() => setBillingConfirmModal({ status: false })}
            loading={markBilledLoading}
          />
        </ModalForm>
      </Modal>
    </Content>
  );
};

export default BillingList;
